import UnfoldLessIcon from '@mui/icons-material/UnfoldLess'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import { Box, IconButton, Tooltip } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import type { DragEvent, KeyboardEvent, MouseEvent } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import GroupsTabAvailableGroupsHeader from '@/features/bidding/components/cascading/activation-groups/GroupsTabAvailableGroupsHeader'
import GroupsTabDynamicIsland from '@/features/bidding/components/cascading/activation-groups/GroupsTabDynamicIsland'
import GroupsTabGroupsList from '@/features/bidding/components/cascading/activation-groups/GroupsTabGroupsList'
import GroupsTabSearchBar from '@/features/bidding/components/cascading/activation-groups/GroupsTabSearchBar'
import UnassignDropZone from '@/features/bidding/components/cascading/activation-groups/UnassignDropZone'
import type { RegulationObjectGroup } from '@/features/bidding/hooks/useBidOfferCascadingDerivedData'
import type { BidLevelInput, DragPayload } from '@/features/bidding/types/bidLadderInput'
import { createGroupDragImage } from '@/features/bidding/utils/createGroupDragImage'
import { wattHoursToMegawattHours } from '@/utils/powerEnergyTransformations'

type GroupsTabContentProps = {
  assignedGroupUuids: Set<string>
  assignGroups: (levelId: string, payload: DragPayload) => void
  bidLevels: BidLevelInput[]
  regulationObjectGroups: RegulationObjectGroup[]
  capacityByActivationGroup: Map<string, number>
  /** Drag payload when dragging from the bid ladder (for showing drop zone) */
  ladderDragPayload?: DragPayload | null
  /** Function to unassign a regulation object from a bid level */
  unassignRegulationObjectGroup?: (levelId: string, regulationObjectCode: string) => void
  /** Called when a drop from the ladder completes (to clear drag state) */
  onLadderDropComplete?: () => void
}

function GroupsTabContent({
  assignedGroupUuids,
  assignGroups,
  bidLevels,
  regulationObjectGroups,
  capacityByActivationGroup,
  ladderDragPayload,
  unassignRegulationObjectGroup,
  onLadderDropComplete,
}: Readonly<GroupsTabContentProps>) {
  const { t } = useTranslation()
  const theme = useTheme()
  const [selectedRegulationObjectCodes, setSelectedRegulationObjectCodes] = useState<string[]>([])
  const [draggingRegulationObjectCodes, setDraggingRegulationObjectCodes] = useState<string[]>([])
  const [lastSelectedRegulationObjectCode, setLastSelectedRegulationObjectCode] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [expandedPanels, setExpandedPanels] = useState<string[]>(() =>
    regulationObjectGroups.map((g) => g.regulationObjectCode),
  )

  // Track previously unassigned codes to detect newly unassigned groups
  const prevUnassignedCodesRef = useRef<Set<string>>(new Set(regulationObjectGroups.map((g) => g.regulationObjectCode)))

  const isFiltering = searchQuery.trim().length > 0
  const hasAssignedGroups = assignedGroupUuids.size > 0

  // Filter out regulation object groups that have ALL their activation groups assigned
  const unassignedRegulationObjectGroups = useMemo(() => {
    return regulationObjectGroups.filter((regGroup) => {
      // Keep the group if at least one activation group is NOT assigned
      return regGroup.activationGroups.some((ag) => !assignedGroupUuids.has(ag.uuid))
    })
  }, [regulationObjectGroups, assignedGroupUuids])

  // Auto-expand newly unassigned groups (groups that come back to available list)
  useEffect(() => {
    const currentUnassignedCodes = new Set(unassignedRegulationObjectGroups.map((g) => g.regulationObjectCode))
    const prevCodes = prevUnassignedCodesRef.current

    // Find groups that are newly unassigned (in current but not in previous)
    const newlyUnassigned = [...currentUnassignedCodes].filter((code) => !prevCodes.has(code))

    if (newlyUnassigned.length > 0) {
      setExpandedPanels((prev) => [...new Set([...prev, ...newlyUnassigned])])
    }

    // Update ref for next comparison
    prevUnassignedCodesRef.current = currentUnassignedCodes
  }, [unassignedRegulationObjectGroups])

  // Clean up selection and dragging state when groups are assigned
  useEffect(() => {
    const unassignedCodes = new Set(unassignedRegulationObjectGroups.map((g) => g.regulationObjectCode))
    setSelectedRegulationObjectCodes((prev) => {
      const filtered = prev.filter((code) => unassignedCodes.has(code))
      return filtered.length === prev.length ? prev : filtered
    })
    setDraggingRegulationObjectCodes((prev) => {
      const filtered = prev.filter((code) => unassignedCodes.has(code))
      return filtered.length === prev.length ? prev : filtered
    })
  }, [unassignedRegulationObjectGroups])

  const allExpanded = expandedPanels.length === unassignedRegulationObjectGroups.length

  // Filter regulation object groups based on search query (case-insensitive)
  // A regulation object is visible if its code or any of its activation group codes match the search
  const filteredRegulationObjectGroups = useMemo(() => {
    if (!searchQuery.trim()) {
      return unassignedRegulationObjectGroups
    }
    const query = searchQuery.toLowerCase().trim()
    return unassignedRegulationObjectGroups.filter(
      (regGroup) =>
        regGroup.regulationObjectCode.toLowerCase().includes(query) ||
        regGroup.activationGroups.some((ag) => ag.code.toLowerCase().includes(query)),
    )
  }, [unassignedRegulationObjectGroups, searchQuery])

  const totalCapacity = regulationObjectGroups.reduce(
    (sum, regGroup) => sum + wattHoursToMegawattHours(regGroup.totalCapacity),
    0,
  )

  // Calculate assigned capacity (groups that have been fully assigned to bid levels)
  const assignedCapacity = useMemo(() => {
    return regulationObjectGroups
      .filter((regGroup) => {
        // Count as assigned if ALL activation groups are assigned
        return regGroup.activationGroups.every((ag) => assignedGroupUuids.has(ag.uuid))
      })
      .reduce((sum, regGroup) => sum + wattHoursToMegawattHours(regGroup.totalCapacity), 0)
  }, [regulationObjectGroups, assignedGroupUuids])

  const selectedCapacity = unassignedRegulationObjectGroups
    .filter((regGroup) => selectedRegulationObjectCodes.includes(regGroup.regulationObjectCode))
    .reduce((sum, regGroup) => sum + wattHoursToMegawattHours(regGroup.totalCapacity), 0)

  const handleExpandToggle = () => {
    if (allExpanded) {
      setExpandedPanels([])
    } else {
      setExpandedPanels(unassignedRegulationObjectGroups.map((g) => g.regulationObjectCode))
    }
  }

  const handleAccordionChange = (panelId: string, isExpanded: boolean) => {
    setExpandedPanels((prev) => (isExpanded ? [...prev, panelId] : prev.filter((id) => id !== panelId)))
  }

  const handleRegulationObjectClick = (regulationObjectCode: string, event: MouseEvent | KeyboardEvent) => {
    const isMultiSelect = event.ctrlKey || event.metaKey
    const isRangeSelect = event.shiftKey

    if (isRangeSelect && lastSelectedRegulationObjectCode) {
      // Range select: select all regulation objects between anchor (last selected) and current
      const anchorIndex = filteredRegulationObjectGroups.findIndex(
        (g) => g.regulationObjectCode === lastSelectedRegulationObjectCode,
      )
      const currentIndex = filteredRegulationObjectGroups.findIndex(
        (g) => g.regulationObjectCode === regulationObjectCode,
      )
      const startIndex = Math.min(anchorIndex, currentIndex)
      const endIndex = Math.max(anchorIndex, currentIndex)
      const rangeCodes = filteredRegulationObjectGroups
        .slice(startIndex, endIndex + 1)
        .map((g) => g.regulationObjectCode)

      setSelectedRegulationObjectCodes(rangeCodes)
      // Don't update lastSelectedRegulationObjectCode on shift-click to keep the anchor
    } else if (isMultiSelect) {
      // Multi-select: toggle the clicked regulation object
      setSelectedRegulationObjectCodes((prev) =>
        prev.includes(regulationObjectCode)
          ? prev.filter((code) => code !== regulationObjectCode)
          : [...prev, regulationObjectCode],
      )
      setLastSelectedRegulationObjectCode(regulationObjectCode)
    } else {
      // Single select: if already selected, deselect; otherwise select only this one
      setSelectedRegulationObjectCodes((prev) =>
        prev.includes(regulationObjectCode) && prev.length === 1 ? [] : [regulationObjectCode],
      )
      setLastSelectedRegulationObjectCode(regulationObjectCode)
    }
  }

  const handleSelectAll = () => {
    const filteredCodes = filteredRegulationObjectGroups.map((regGroup) => regGroup.regulationObjectCode)
    setSelectedRegulationObjectCodes(filteredCodes)
  }

  const handleDeselectAll = () => {
    setSelectedRegulationObjectCodes([])
  }

  const handleDragStart = (regulationObjectCode: string, event: DragEvent) => {
    // Determine which regulation objects to drag
    const codesToDrag = selectedRegulationObjectCodes.includes(regulationObjectCode)
      ? selectedRegulationObjectCodes // Drag all selected regulation objects
      : [regulationObjectCode] // Drag only this regulation object

    setDraggingRegulationObjectCodes(codesToDrag)

    // Collect detailed activation group data for the drag payload (only unassigned groups)
    const draggedGroups = unassignedRegulationObjectGroups
      .filter((regGroup) => codesToDrag.includes(regGroup.regulationObjectCode))
      .flatMap((regGroup) =>
        regGroup.activationGroups.map((ag) => ({
          uuid: ag.uuid,
          code: ag.code,
          regulationObjectCode: regGroup.regulationObjectCode,
          marketProgram: ag.marketProgram,
          capacityMW: capacityByActivationGroup.get(ag.uuid) ?? 0,
        })),
      )

    const payload: DragPayload = {
      regulationObjectCodes: codesToDrag,
      activationGroups: draggedGroups,
    }

    // Set data for the drag operation
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('application/json', JSON.stringify(payload))

    // Calculate total capacity for dragged regulation objects
    const draggedCapacity = unassignedRegulationObjectGroups
      .filter((regGroup) => codesToDrag.includes(regGroup.regulationObjectCode))
      .reduce((sum, regGroup) => sum + wattHoursToMegawattHours(regGroup.totalCapacity), 0)

    // Set custom drag image showing count and total capacity if multiple
    if (codesToDrag.length > 1) {
      createGroupDragImage({
        event,
        groupCount: codesToDrag.length,
        totalCapacity: draggedCapacity,
        theme,
      })
    }
  }

  const handleDragEnd = () => {
    // Clear dragged codes from selection when drag ends
    // This prevents groups from appearing as selected when they reappear after being unassigned
    if (draggingRegulationObjectCodes.length > 0) {
      setSelectedRegulationObjectCodes((prev) => prev.filter((code) => !draggingRegulationObjectCodes.includes(code)))
    }
    setDraggingRegulationObjectCodes([])
  }

  const handleAssignToLevel = (levelId: string) => {
    if (selectedRegulationObjectCodes.length === 0) return

    // Collect detailed activation group data for the assignment (only unassigned groups)
    const selectedGroups = unassignedRegulationObjectGroups
      .filter((regGroup) => selectedRegulationObjectCodes.includes(regGroup.regulationObjectCode))
      .flatMap((regGroup) =>
        regGroup.activationGroups.map((ag) => ({
          uuid: ag.uuid,
          code: ag.code,
          regulationObjectCode: regGroup.regulationObjectCode,
          marketProgram: ag.marketProgram,
          capacityMW: capacityByActivationGroup.get(ag.uuid) ?? 0,
        })),
      )

    const payload: DragPayload = {
      regulationObjectCodes: selectedRegulationObjectCodes,
      activationGroups: selectedGroups,
    }

    assignGroups(levelId, payload)
    setSelectedRegulationObjectCodes([])
  }

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <GroupsTabSearchBar value={searchQuery} onChange={setSearchQuery} />

      <Box sx={{ display: 'flex', alignItems: 'center', height: '30px', justifyContent: 'space-between', mb: 2 }}>
        <GroupsTabAvailableGroupsHeader
          allGroupsSize={unassignedRegulationObjectGroups.length}
          filteredGroupsSize={filteredRegulationObjectGroups.length}
          isFiltering={isFiltering}
          totalCapacity={totalCapacity}
        />
        {unassignedRegulationObjectGroups.length > 0 && (
          <Tooltip
            title={
              allExpanded
                ? t('bidding.bidding_cascading.groups_tab.actions.collapse_all')
                : t('bidding.bidding_cascading.groups_tab.actions.expand_all')
            }
          >
            <IconButton
              aria-label={t('bidding.bidding_cascading.groups_tab.actions.expand_collapse')}
              size="small"
              onClick={handleExpandToggle}
            >
              {allExpanded ? <UnfoldLessIcon fontSize="small" /> : <UnfoldMoreIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <GroupsTabDynamicIsland
        assignedCapacity={assignedCapacity}
        bidLevels={bidLevels}
        filteredCount={filteredRegulationObjectGroups.length}
        selectedCapacity={selectedCapacity}
        selectedCount={selectedRegulationObjectCodes.length}
        totalCapacity={totalCapacity}
        onAssignToLevel={handleAssignToLevel}
        onClearSelection={handleDeselectAll}
        onSelectAll={handleSelectAll}
      />

      {unassignRegulationObjectGroup && onLadderDropComplete && (
        <UnassignDropZone
          hasAssignedGroups={hasAssignedGroups}
          ladderDragPayload={ladderDragPayload ?? null}
          unassignRegulationObjectGroup={unassignRegulationObjectGroup}
          onDropComplete={onLadderDropComplete}
        />
      )}

      <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto' }}>
        <GroupsTabGroupsList
          capacityByActivationGroup={capacityByActivationGroup}
          draggingRegulationObjectCodes={draggingRegulationObjectCodes}
          expandedPanels={expandedPanels}
          filteredRegulationObjectGroups={filteredRegulationObjectGroups}
          hasAnyGroups={regulationObjectGroups.length > 0}
          regulationObjectGroups={unassignedRegulationObjectGroups}
          searchQuery={searchQuery}
          selectedRegulationObjectCodes={selectedRegulationObjectCodes}
          onAccordionChange={handleAccordionChange}
          onClearSearch={() => setSearchQuery('')}
          onDragEnd={handleDragEnd}
          onDragStart={handleDragStart}
          onRegulationObjectClick={handleRegulationObjectClick}
        />
      </Box>
    </Box>
  )
}

export default GroupsTabContent
